import debounce from "debounce";

import {isServer} from "../runtime";

/**
 * Check if browser supports dvh units
 */
function supportsDvh(): boolean {
    if (isServer() || typeof CSS === "undefined") return false;

    return CSS.supports("height", "100dvh");
}

/**
 * Measure real viewport height
 * Uses window.innerHeight which doesn't include keyboard in most modern browsers
 */
function measureViewportHeight(): number {
    if (isServer()) return 0;

    // window.innerHeight doesn't change when keyboard opens on most mobile browsers
    return window.innerHeight;
}

/**
 * Update CSS custom property with current viewport height
 * Updates --real-viewport-height variable on document root
 */
function updateViewportHeight(): void {
    const height = measureViewportHeight();
    document.documentElement.style.setProperty("--real-viewport-height", `${height}px`);
}

/**
 * Debounced version of viewport height update
 */
const debouncedUpdate = debounce(updateViewportHeight, 100);

/**
 * Initialize viewport height tracking
 * Updates CSS variable --real-viewport-height automatically
 * Only runs if browser doesn't support dvh (used as fallback)
 * Call once in your app root component
 */
export function initViewportHeight(): void {
    if (isServer()) return;

    if (supportsDvh()) {
        return;
    }

    // Set initial value
    updateViewportHeight();

    window.addEventListener("resize", debouncedUpdate);

    window.addEventListener("orientationchange", updateViewportHeight);
}

/**
 * Cleanup viewport height tracking
 * Call when unmounting app
 */
export function cleanupViewportHeight(): void {
    if (isServer()) return;

    window.removeEventListener("resize", debouncedUpdate);
    window.removeEventListener("orientationchange", updateViewportHeight);
}
